import React from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from './components/AppLayout';
import './LandingPage.css';

const MainMenuLongTerm = ({signOut}) => {
  const navigate = useNavigate();

  const navigateTo = (path) => {
    navigate(path);
  };

  const ActionButton = ({ onClick, title, tooltip }) => (
    <button
      onClick={onClick}
      title={tooltip}
      style={{
        padding: '12px 16px',
        background: 'rgba(59, 130, 246, 0.1)',
        border: '1px solid rgba(59, 130, 246, 0.3)',
        borderRadius: '8px',
        color: '#3b82f6',
        fontSize: '14px',
        fontWeight: '500',
        cursor: 'pointer',
        transition: 'all 0.2s',
        textAlign: 'center',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'rgba(59, 130, 246, 0.2)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)';
      }}
    >
      {title}
    </button>
  );

  return (
    <AppLayout signOut={signOut}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Page Title */}
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: '700', color: 'rgba(0, 0, 0, 0.9)' }}>
            Long-Term Strategy Analysis
          </h1>
        </div>

        {/* 3-Column Grid Container */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
          {/* ETFs */}
          <div className="landing-panel">
            <div className="landing-panel-header">
              <h2 className="landing-panel-title">ETFs</h2>
            </div>
            <div className="landing-panel-content">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <ActionButton onClick={() => navigateTo('/list')} title="List" tooltip="View all available ETFs" />
                <ActionButton onClick={() => navigateTo('/runAllLongTerm')} title="Analysis" tooltip="Run analysis on all ETFs" />
              </div>
            </div>
          </div>

          {/* Market Movers */}
          <div className="landing-panel">
            <div className="landing-panel-header">
              <h2 className="landing-panel-title">Market Movers</h2>
            </div>
            <div className="landing-panel-content">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <ActionButton
                  onClick={() => navigateTo('/runMostActiveLongTerm')}
                  title="Most Active"
                  tooltip="Stocks ordered in descending order by intraday trade volume"
                />
                <ActionButton
                  onClick={() => navigateTo('/runDayGainersLongTerm')}
                  title="Most Gainers"
                  tooltip="Stocks ordered in descending order by price percent change"
                />
                <ActionButton
                  onClick={() => navigateTo('/runDayLosersLongTerm')}
                  title="Most Losers"
                  tooltip="Stocks ordered in ascending order by price percent change"
                />
                <ActionButton
                  onClick={() => navigateTo('/runhigh_volumeLongTerm')}
                  title="High Volume"
                  tooltip="Stocks with strong volume gains from previous session"
                />
                <ActionButton
                  onClick={() => navigateTo('/runhot_stocksLongTerm')}
                  title="Hot Stocks"
                  tooltip="High-performing stocks with BUY rating and robust technical support"
                />
                <ActionButton
                  onClick={() => navigateTo('/runmoversUpLongTerm')}
                  title="Movers Up"
                  tooltip="Market movers up in today's trading session"
                />
                <ActionButton
                  onClick={() => navigateTo('/runmoversDownLongTerm')}
                  title="Movers Down"
                  tooltip="Market movers down in today's trading session"
                />
              </div>
            </div>
          </div>

          {/* Technical Signals & Indicators */}
          <div className="landing-panel">
            <div className="landing-panel-header">
              <h2 className="landing-panel-title">Technical Signals & Indicators</h2>
            </div>
            <div className="landing-panel-content">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <ActionButton
                  onClick={() => navigateTo('/rungolden_crossLongTerm')}
                  title="Golden Cross"
                  tooltip="50-day MA crosses above 200-day MA"
                />
                <ActionButton
                  onClick={() => navigateTo('/rundeath_crossLongTerm')}
                  title="Death Cross"
                  tooltip="50-day MA crosses below 200-day MA"
                />
                <ActionButton
                  onClick={() => navigateTo('/runj_patternLongTerm')}
                  title="J Pattern"
                  tooltip="Up-trending price action that dips before resuming trend"
                />
                <ActionButton
                  onClick={() => navigateTo('/runconsolidationLongTerm')}
                  title="Consolidation"
                  tooltip="Stocks consolidating near highs within uptrend"
                />
                <ActionButton
                  onClick={() => navigateTo('/runrsi_overboughtLongTerm')}
                  title="RSI Overbought"
                  tooltip="20-Day RSI above 70, potential overbought conditions"
                />
                <ActionButton
                  onClick={() => navigateTo('/runrsi_oversoldLongTerm')}
                  title="RSI Oversold"
                  tooltip="20-Day RSI below 30, potential oversold conditions"
                />
                <ActionButton
                  onClick={() => navigateTo('/runpenny_gapLongTerm')}
                  title="Penny Gap"
                  tooltip="Penny stocks with highest percentage change over last 5 days"
                />
              </div>
            </div>
          </div>

          {/* Value, Fundamentals & Income */}
          <div className="landing-panel">
            <div className="landing-panel-header">
              <h2 className="landing-panel-title">Value, Fundamentals & Income</h2>
            </div>
            <div className="landing-panel-content">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <ActionButton
                  onClick={() => navigateTo('/rundividendLongTerm')}
                  title="Dividend"
                  tooltip="Dividend-paying stocks with bullish signals and strong momentum"
                />
                <ActionButton
                  onClick={() => navigateTo('/runtop_fundamentalsLongTerm')}
                  title="Top Fundamentals"
                  tooltip="Stocks with strong sales growth, cash flow, and shareholder equity"
                />
                <ActionButton
                  onClick={() => navigateTo('/runUndervaluedLongTerm')}
                  title="Undervalued Large Caps"
                  tooltip="Large cap stocks that are potentially undervalued"
                />
                <ActionButton
                  onClick={() => navigateTo('/runUnderValuedGrowthLongTerm')}
                  title="Undervalued Growth"
                  tooltip="Earnings growth >25% with low PE and PEG ratios"
                />
                <ActionButton
                  onClick={() => navigateTo('/runincome_growthLongTerm')}
                  title="Income Growth"
                  tooltip="High dividends and earnings growth with solid yields"
                />
              </div>
            </div>
          </div>

          {/* Themes & Sector Focus */}
          <div className="landing-panel">
            <div className="landing-panel-header">
              <h2 className="landing-panel-title">Themes & Sector Focus</h2>
            </div>
            <div className="landing-panel-content">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <ActionButton
                  onClick={() => navigateTo('/runGrowthTechnologyLongTerm')}
                  title="Growth Technology"
                  tooltip="Technology stocks with revenue and earnings growth >25%"
                />
                <ActionButton
                  onClick={() => navigateTo('/runtop_techLongTerm')}
                  title="Top Tech"
                  tooltip="Computer and tech stocks hitting new 6-month highs"
                />
                <ActionButton
                  onClick={() => navigateTo('/rundefensive_stockLongTerm')}
                  title="Defensive Stock"
                  tooltip="Reliable dividends and sustained growth regardless of market"
                />
              </div>
            </div>
          </div>

          {/* Small Caps & Speculative */}
          <div className="landing-panel">
            <div className="landing-panel-header">
              <h2 className="landing-panel-title">Small Caps & Speculative</h2>
            </div>
            <div className="landing-panel-content">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <ActionButton
                  onClick={() => navigateTo('/runAggressiveLongTerm')}
                  title="Small Caps Aggressive"
                  tooltip="Small cap stocks with earnings growth rates >25%"
                />
                <ActionButton
                  onClick={() => navigateTo('/runSmallLongTerm')}
                  title="Small Caps Gainers"
                  tooltip="Small caps with 1 day price change of 5.0% or more"
                />
                <ActionButton
                  onClick={() => navigateTo('/runtop_under_10LongTerm')}
                  title="Top Under 10"
                  tooltip="Stocks under $10 with strong P/E and P/S ratios"
                />
              </div>
            </div>
          </div>

          {/* Long-Term & Breakout Candidates */}
          <div className="landing-panel">
            <div className="landing-panel-header">
              <h2 className="landing-panel-title">Long-Term & Breakout Candidates</h2>
            </div>
            <div className="landing-panel-content">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <ActionButton
                  onClick={() => navigateTo('/runbuy_longtermLongTerm')}
                  title="Buy Longterm"
                  tooltip="Bullish stocks maintaining a 100% Overall Buy Signal"
                />
                <ActionButton
                  onClick={() => navigateTo('/run52wk_toppicksLongTerm')}
                  title="52wk Top Picks"
                  tooltip="New 52-Week High with >10% price change over past month"
                />
              </div>
            </div>
          </div>

          {/* Strategy Builder */}
          <div className="landing-panel">
            <div className="landing-panel-header">
              <h2 className="landing-panel-title">Strategy Builder</h2>
            </div>
            <div className="landing-panel-content">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <ActionButton
                  onClick={() => navigateTo('/runOneLongTerm')}
                  title="Custom List"
                  tooltip="Analyze a custom list of securities"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default MainMenuLongTerm;
